import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiClient } from "../../lib/api";
import {
  cacheAppToken,
  clearCachedAppToken,
  clearCachedSandboxToken,
  readCachedAppToken,
} from "../../lib/appTokenStore";
import { Entitlements } from "../../lib/entitlements";

export interface AppUser {
  id: string;
  orgId: string;
  permissions: string[];
  /** Plan feature matrix resolved by the API for the user's organization. */
  entitlements?: Entitlements;
  emailVerified: boolean;
  email: string;
  name: string;
  zitadelUserId?: string;
  /** True for anonymous read-only sandbox (demo) sessions. */
  sandbox?: boolean;
  /** True on the very first authenticated request after JIT provisioning.
   *  The frontend must perform a silent OIDC token refresh before continuing
   *  so subsequent JWTs carry the newly-stamped role claims. */
  is_new_signup?: boolean;
}

interface AuthState {
  appToken: string | null;
  user: AppUser | null;
}

const initialState: AuthState = {
  // Bootstrap from the cached token so the API interceptor has something to
  // attach before ProtectedRoute refreshes it from the OIDC context.
  appToken: readCachedAppToken(),
  user: null,
};

// Thunk for logout: calls backend to clear any legacy cookie, then clears state.
export const logoutThunk = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch }) => {
    try {
      await apiClient.post("/auth/logout");
    } catch {}
    dispatch(logout());
  },
);

// Re-fetches /auth/me so permissions and plan entitlements reflect the latest
// server state, e.g. immediately after a subscription change.
export const refreshUserThunk = createAsyncThunk(
  "auth/refreshUser",
  async (_, { dispatch }) => {
    const { data } = await apiClient.get("/auth/me");
    dispatch(setUser({ ...data, is_new_signup: false }));
  },
);

const appAuthSlice = createSlice({
  name: "appAuth",
  initialState,
  reducers: {
    setAppToken(state, action: PayloadAction<string>) {
      state.appToken = action.payload;
      // Keep a cached copy so a hard refresh doesn't lose the token before
      // ProtectedRoute can re-hydrate it. A sandbox token is cached per tab so
      // it never displaces a logged-in user's token on the same origin.
      cacheAppToken(action.payload);
    },
    setUser(state, action: PayloadAction<AppUser | null>) {
      state.user = action.payload;
    },
    /**
     * Ends an anonymous sandbox session. A demo tab shares its origin with any
     * logged-in tab, so this clears only the tab-scoped demo token — a full
     * `logout()` here would evict the real user's cached session and their
     * user-scoped keys along with it.
     */
    exitSandboxSession(state) {
      state.appToken = null;
      state.user = null;
      clearCachedSandboxToken();
    },
    logout(state) {
      state.appToken = null;
      state.user = null;
      clearCachedAppToken();

      // Clear user-scoped keys so they don't leak across sessions.
      try {
        const knownKeys = [
          "insaion_onboarding_complete",
          "insaion_onboarding_in_progress",
          "insaion_onboarding_completed_steps",
          "lastVisitedDashboardId",
          "lastActivityAt",
          "alarms.lastTab",
          "sandboxMode",
          "sandboxStartTime",
          "sandboxLeadCaptured",
        ];
        knownKeys.forEach((k) => {
          localStorage.removeItem(k);
          sessionStorage.removeItem(k);
        });
        const clearByPrefix = (storage: Storage, prefix: string) => {
          for (let i = storage.length - 1; i >= 0; i--) {
            const key = storage.key(i);
            if (key && key.startsWith(prefix)) storage.removeItem(key);
          }
        };
        clearByPrefix(localStorage, "insaion_onboarding_");
        clearByPrefix(sessionStorage, "insaion_onboarding_");
      } catch {}
    },
  },
});

export const { exitSandboxSession, logout, setAppToken, setUser } =
  appAuthSlice.actions;

export const appAuthReducer = appAuthSlice.reducer;

export const selectAppToken = (state: { appAuthReducer: AuthState }) =>
  state.appAuthReducer.appToken;

export const selectAppUser = (state: { appAuthReducer: AuthState }) =>
  state.appAuthReducer.user;

// Flat list of permissions resolved from the Zitadel token / DB role mapping.
export const selectPermissions = (state: {
  appAuthReducer: AuthState;
}): string[] => state.appAuthReducer.user?.permissions ?? [];
